import { useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Checkbox, TextInput } from '../../components/ui.tsx';
import { requestAssertion, wasDismissed } from '../../lib/webauthn.ts';
import {
  isPartnerMfaChallenge,
  partnerApi,
  PartnerApiError,
  type PartnerChallengeOption,
  type PartnerLoginResponse,
} from '../../api/partner.ts';
import { PARTNER_SESSION_QUERY_KEY, usePartnerIdentity } from '../../components/partner-layout.tsx';
import { useDocumentTitle } from '../../hooks/index.ts';

export default function PartnerLoginPage() {
  useDocumentTitle('Partner sign in');
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const identity = usePartnerIdentity();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [challenge, setChallenge] = useState<Extract<PartnerLoginResponse, { mfaRequired: true }> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // A `?from` handed over by `RequirePartner`, kept inside the portal: a
  // sign-in must never be a way to be redirected somewhere else.
  const from = (location.state as { from?: string } | null)?.from;
  const returnTo = from && from.startsWith('/partner') ? from : undefined;

  /**
   * Where a completed sign-in lands: the portal, always.
   *
   * It used to be the security screen unconditionally, which is what made an
   * optional feature feel compulsory — the only page a partner ever reached
   * was the one asking them to set up two-factor authentication. The shell
   * still diverts an account that a MANDATE applies to, and only then.
   */
  const finish = async () => {
    // REMOVED, not invalidated. Invalidation only refetches queries something
    // is currently observing, so the previous partner's customers, commissions
    // and payouts would sit in the cache and be handed straight to each page
    // as it mounted — another partner's figures on screen while the new ones
    // loaded behind them. Signing in on a shared browser is exactly when that
    // happens.
    queryClient.removeQueries({ queryKey: ['partner'] });
    // Then awaited, not fired and forgotten: `RequirePartner` reads the
    // session query, and arriving before it had answered would show it the
    // 401 from a moment ago and bounce straight back here.
    await queryClient.refetchQueries({ queryKey: PARTNER_SESSION_QUERY_KEY });
    navigate(returnTo ?? '/partner', { replace: true });
  };

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await partnerApi.login({ email, password });
      if (isPartnerMfaChallenge(result)) setChallenge(result);
      else await finish();
    } catch (caught) {
      setError(caught instanceof PartnerApiError ? caught.message : 'Could not sign in. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  // The mirror image of `RequirePartner`, and deliberately the opposite
  // condition: that guard leaves the portal only on a 401, this one enters it
  // only on a success. One resolution of one shared query can satisfy at most
  // one of them, so the pair cannot take turns redirecting — which is what a
  // "kept flashing" screen actually is.
  //
  // Not gated on `isLoading`: a signed-out visitor is the common case here,
  // and making everybody watch a spinner for a 401 to arrive would be a worse
  // sign-in page than briefly showing a form to the rare partner who is
  // already signed in.
  //
  // `isSuccess`, though, and not merely `data`. React Query keeps the last
  // successful value alongside a new error, so a session that ended mid-visit
  // leaves `data` populated next to the 401 that `RequirePartner` redirects
  // on — and reading `data` here would have sent them straight back, which is
  // the login/portal loop this whole guard pair exists to prevent.
  if (identity.isSuccess && !challenge) return <Navigate to={returnTo ?? '/partner'} replace />;

  if (challenge)
    return <PartnerMfaForm challenge={challenge} onDone={() => finish()} onCancel={() => setChallenge(null)} />;

  return (
    <div className="flex min-h-screen items-center justify-center bg-[color:var(--color-surface)] px-4 py-12">
      <main className="w-full max-w-md">
        <img src="/BlastoClean-Logo.svg" alt="BlastoClean" className="mx-auto mb-6 h-10 w-auto" />
        <div className="card">
          <h1 className="text-2xl font-bold">Partner portal</h1>
          <p className="mb-6 mt-1 text-[color:var(--color-muted)]">Sign in to manage your account and security.</p>
          <form onSubmit={submit} noValidate>
            {error ? (
              <div className="mb-4">
                <Alert tone="error">{error}</Alert>
              </div>
            ) : null}
            <TextInput
              label="Email"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
            <TextInput
              label="Password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
            <Button type="submit" className="w-full" busy={busy}>
              Sign in
            </Button>
          </form>
        </div>
      </main>
    </div>
  );
}

function PartnerMfaForm({
  challenge,
  onDone,
  onCancel,
}: {
  challenge: Extract<PartnerLoginResponse, { mfaRequired: true }>;
  onDone: () => void | Promise<void>;
  onCancel: () => void;
}) {
  const [option, setOption] = useState<PartnerChallengeOption>(challenge.options[0]!);
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [rememberDevice, setRememberDevice] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function start(next: PartnerChallengeOption) {
    setOption(next);
    setChallengeId(null);
    setCode('');
    setError(null);
    setBusy(true);
    try {
      const started = await partnerApi.loginChallenge({
        loginToken: challenge.loginToken,
        type: next.type,
        methodId: next.methodId,
      });
      setChallengeId(started.challengeId);
      if (next.type === 'webauthn' && started.publicKey) {
        const assertion = await requestAssertion(started.publicKey);
        if (assertion) {
          await partnerApi.loginVerify({
            loginToken: challenge.loginToken,
            challengeId: started.challengeId,
            assertion,
            rememberDevice,
          });
          await onDone();
        }
      }
    } catch (caught) {
      if (!wasDismissed(caught))
        setError(caught instanceof PartnerApiError ? caught.message : 'Could not verify that method.');
      setChallengeId(null);
    } finally {
      setBusy(false);
    }
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!challengeId) return void start(option);
    setBusy(true);
    setError(null);
    try {
      await partnerApi.loginVerify({ loginToken: challenge.loginToken, challengeId, code, rememberDevice });
      await onDone();
    } catch (caught) {
      setError(caught instanceof PartnerApiError ? caught.message : 'Could not verify that code.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-[color:var(--color-surface)] px-4 py-12">
      <main className="w-full max-w-md">
        <div className="card">
          <h1 className="text-2xl font-bold">Verify it’s you</h1>
          <p className="mt-1 text-[color:var(--color-muted)]">Choose one of your security methods.</p>
          <form className="mt-5" onSubmit={submit}>
            {error ? (
              <div className="mb-4">
                <Alert tone="error">{error}</Alert>
              </div>
            ) : null}
            <fieldset className="mb-4">
              <legend className="mb-2 text-sm font-medium">Method</legend>
              {challenge.options.map((candidate) => (
                <label
                  key={`${candidate.type}:${candidate.methodId ?? 'none'}`}
                  className="mb-2 flex items-center gap-2 text-sm"
                >
                  <input
                    type="radio"
                    checked={option.type === candidate.type && option.methodId === candidate.methodId}
                    onChange={() => void start(candidate)}
                  />
                  {candidate.label}
                </label>
              ))}
            </fieldset>
            {challengeId && option.type !== 'webauthn' ? (
              <TextInput
                label={option.type === 'recovery' ? 'Recovery code' : 'Code'}
                value={code}
                onChange={(event) => setCode(event.target.value)}
                inputMode={option.type === 'recovery' ? 'text' : 'numeric'}
                autoComplete={option.type === 'recovery' ? 'off' : 'one-time-code'}
              />
            ) : null}
            {challenge.trustedDeviceDays > 0 ? (
              <Checkbox
                label={`Remember this browser for ${challenge.trustedDeviceDays} days`}
                checked={rememberDevice}
                onChange={(event) => setRememberDevice(event.target.checked)}
              />
            ) : null}
            {option.type === 'webauthn' ? (
              <Button type="button" className="w-full" busy={busy} onClick={() => void start(option)}>
                Use security key
              </Button>
            ) : (
              <Button type="submit" className="w-full" busy={busy}>
                {challengeId ? 'Verify' : 'Send code'}
              </Button>
            )}
          </form>
          <button
            type="button"
            className="mt-5 text-sm text-[color:var(--color-brand)] hover:underline"
            onClick={onCancel}
          >
            Start over
          </button>
        </div>
      </main>
    </div>
  );
}
